import type { JSX } from "react";
import { Link } from "react-router";

interface Props {
  /**
   * "app" (default): the authenticated app's own catch-all (`appRoutes`) —
   * relies on the `AppShell` ancestor for a full-viewport height, and
   * invites a signed-in reader back to their work.
   *
   * "public": the docs host's own catch-all (`publicDocsRoutes`) — there is
   * no `AppShell` ancestor here to supply height (mirrors `PublicDocsPage`'s
   * own `h-dvh` wrapper) and no signed-in reader to address, so the copy
   * offers a link to the documentation home instead of "continuar
   * trabajando".
   */
  variant?: "app" | "public";
}

/**
 * Catch-all page for URLs that match no route — wired as the `path: "*"` entry
 * in `router.tsx` (both `appRoutes` and `publicDocsRoutes`). Without it
 * react-router throws a 404 `ErrorResponse`, which `RouteErrorFallback`
 * renders as "Algo salió mal" — a crash, not a wrong URL.
 */
export default function NotFoundPage({ variant = "app" }: Props = {}): JSX.Element {
  const isPublic = variant === "public";
  return (
    <section
      className={`grid ${isPublic ? "h-dvh" : "min-h-full"} place-items-center px-4 py-12 text-foreground`}
    >
      <div className="w-full max-w-md rounded-md border border-border bg-card p-6 text-card-foreground shadow-sm">
        <p className="text-label-2 uppercase text-muted-foreground">Error 404</p>
        <h1 className="mt-2 text-heading-2">Página no encontrada</h1>
        <p className="mt-3 text-body-2 text-muted-foreground">
          {isPublic
            ? "La dirección que abriste no existe o se movió a otro lugar. Revisa el enlace o vuelve al inicio para consultar la documentación."
            : "La dirección que abriste no existe o se movió a otro lugar. Revisa el enlace o vuelve al inicio para continuar trabajando."}
        </p>
        <div className="mt-5">
          <Link
            to="/"
            className="inline-block rounded-md border border-border px-4 py-2 text-body-2 font-medium text-foreground hover:bg-muted"
          >
            {isPublic ? "Ir a la documentación" : "Volver al inicio"}
          </Link>
        </div>
      </div>
    </section>
  );
}
